import { useMemo } from 'react'
import {
  DashboardFilledIcon,
  DashboardOutlinedIcon,
  ListFilledIcon,
  ListOutlinedIcon,
  LoudspeakerOutlinedIcon,
  MessageAddFilledIcon,
  MessageAddOutlinedIcon,
  MessageDotsFilledIcon,
  MessageDotsOutlinedIcon,
  MessageFeedbackFilledIcon,
  MessageFeedbackOutlinedIcon,
  PeopleFilledIcon,
  PeopleOutlinedIcon,
  QuestionOutlinedIcon,
  SettingsFilledIcon,
  SettingsOutlinedIcon,
} from '@getgo/chameleon-icons/react'
import type { ContactCenterUserContext as UserContext } from '@ucc/admin-global-state'
import { Permissions } from '@ucc/admin-global-state'
import { CALL_QUEUES_ROUTE } from '@ucc/unified-admin-common'

import { SidebarLink } from 'types/SidebarLink'
import { Privileges } from 'utils/rolesToPrivilegesMapper'

const useCallQueuesLinks = ({
  userContext,
  SkeletonLink,
  t,
}: {
  userContext: UserContext | null
  SkeletonLink: { key: string }
  t: (key: string) => string
}) => {
  return useMemo(() => {
    const CallQueuesDashboardLink: SidebarLink = {
      htmlId: 'call-queues-dashboard',
      key: 'call-queues',
      to: `/${CALL_QUEUES_ROUTE}/dashboard`,
      primary: t('Dashboard'),
      activeIcon: <DashboardFilledIcon />,
      inactiveIcon: <DashboardOutlinedIcon />,
      privilege: Privileges.CallQueueLinks,
      globalSearchBarDetails: [
        {
          keyword: t('Contact center'),
          to: `/${CALL_QUEUES_ROUTE}/dashboard`,
          tags: [t('Dashboard')],
        },
      ],
    }

    const CallQueuesQueuesLink: SidebarLink = {
      htmlId: 'call-queues-queues',
      key: 'call-queues',
      to: `/${CALL_QUEUES_ROUTE}/queues`,
      primary: t('Call queues'),
      activeIcon: <ListFilledIcon />,
      inactiveIcon: <ListOutlinedIcon />,
      privilege: Privileges.CallQueueLinks,
      globalSearchBarDetails: [
        {
          keyword: t('Call queues'),
          to: `/${CALL_QUEUES_ROUTE}/queues`,
        },
      ],
    }

    const CallQueuesChatQueuesLink: SidebarLink = {
      htmlId: 'call-queues-chat-queues',
      key: 'call-queues',
      to: `/${CALL_QUEUES_ROUTE}/chat-queues`,
      primary: t('Inbox queues'),
      activeIcon: <MessageAddFilledIcon />,
      inactiveIcon: <MessageAddOutlinedIcon />,
      privilege: Privileges.CallQueueLinks,
      globalSearchBarDetails: [
        {
          keyword: t('Inbox queues'),
          to: `/${CALL_QUEUES_ROUTE}/chat-queues`,
        },
      ],
    }

    const CallQueuesCampaignsLink: SidebarLink = {
      htmlId: 'call-queues-campaigns',
      key: 'call-queues',
      to: `/${CALL_QUEUES_ROUTE}/call-campaigns`,
      primary: t('Campaigns'),
      activeIcon: <LoudspeakerOutlinedIcon />,
      inactiveIcon: <LoudspeakerOutlinedIcon />,
      privilege: Privileges.CallQueueLinks,
      globalSearchBarDetails: [
        {
          keyword: t('Call campaigns'),
          to: `/${CALL_QUEUES_ROUTE}/call-campaigns`,
        },
      ],
    }

    const ChatAssistantsLink: SidebarLink = {
      htmlId: 'call-queues-chat-assistants',
      key: 'call-queues',
      to: `/${CALL_QUEUES_ROUTE}/chat-assistants`,
      primary: t('Chat Assistants'),
      activeIcon: <MessageDotsFilledIcon />,
      inactiveIcon: <MessageDotsOutlinedIcon />,
      privilege: Privileges.CallQueueLinks,
      globalSearchBarDetails: [
        {
          keyword: t('Chat Assistants'),
          to: `/${CALL_QUEUES_ROUTE}/chat-assistants`,
        },
      ],
    }

    const CallQueuesFlagsLink: SidebarLink = {
      htmlId: 'call-queues-flags',
      key: 'call-queues',
      to: `/${CALL_QUEUES_ROUTE}/flags`,
      primary: t('Flags'),
      badge: t('Beta'),
      activeIcon: <MessageFeedbackFilledIcon />,
      inactiveIcon: <MessageFeedbackOutlinedIcon />,
      privilege: Privileges.CallQueueLinks,
      globalSearchBarDetails: [
        {
          keyword: t('Flags'),
          to: `/${CALL_QUEUES_ROUTE}/flags`,
        },
      ],
    }

    const SurveysLink: SidebarLink = {
      htmlId: 'call-queues-surveys',
      key: 'call-queues',
      to: `/${CALL_QUEUES_ROUTE}/surveys`,
      primary: t('Surveys'),
      badge: t('New'),
      activeIcon: <QuestionOutlinedIcon />,
      inactiveIcon: <QuestionOutlinedIcon />,
      privilege: Privileges.CallQueueLinks,
      globalSearchBarDetails: [
        {
          keyword: t('Surveys'),
          to: `/${CALL_QUEUES_ROUTE}/surveys`,
        },
      ],
    }

    const CallQueuesUsersLink: SidebarLink = {
      htmlId: 'call-queues-users',
      key: 'call-queues',
      to: `/${CALL_QUEUES_ROUTE}/users`,
      primary: t('Users'),
      activeIcon: <PeopleFilledIcon />,
      inactiveIcon: <PeopleOutlinedIcon />,
      privilege: Privileges.CallQueueLinks,
      globalSearchBarDetails: [
        {
          keyword: t('Contact center users'),
          to: `/${CALL_QUEUES_ROUTE}/users`,
        },
      ],
    }

    const CallQueuesSettingsLink: SidebarLink = {
      htmlId: 'call-queues-settings',
      key: 'call-queues',
      to: `/${CALL_QUEUES_ROUTE}/settings`,
      primary: t('General settings'),
      activeIcon: <SettingsFilledIcon />,
      inactiveIcon: <SettingsOutlinedIcon />,
      privilege: Privileges.CallQueueLinks,
      globalSearchBarDetails: [
        {
          keyword: t('Contact center settings'),
          to: `/${CALL_QUEUES_ROUTE}/settings`,
        },
      ],
    }

    const CallQueuesLinks: SidebarLink[] = userContext === null ? new Array(6).fill(SkeletonLink) : []

    if (userContext?.permissions?.includes(Permissions.OVERVIEW_VIEW)) {
      CallQueuesLinks.push(CallQueuesDashboardLink)
    }

    if (userContext?.permissions?.includes(Permissions.CALL_QUEUES_VIEW)) {
      CallQueuesLinks.push(CallQueuesQueuesLink)
    }

    if (
      userContext?.permissions?.includes(Permissions.CHAT_QUEUES_VIEW) &&
      !userContext?.license?.startsWith('GOTO_CE')
    ) {
      CallQueuesLinks.push(CallQueuesChatQueuesLink)
    }

    if (userContext?.permissions?.includes(Permissions.CALL_CAMPAIGNS_VIEW)) {
      CallQueuesLinks.push(CallQueuesCampaignsLink)
    }

    if (
      userContext?.permissions?.includes(Permissions.SMART_RESPONSES_VIEW) &&
      !userContext?.license?.startsWith('GOTO_CE')
    ) {
      CallQueuesLinks.push(ChatAssistantsLink)
    }

    if (userContext?.permissions?.includes(Permissions.FLAGS_VIEW)) {
      CallQueuesLinks.push(CallQueuesFlagsLink)
    }

    if (userContext?.permissions?.includes(Permissions.SURVEYS_VIEW)) {
      CallQueuesLinks.push(SurveysLink)
    }

    if (userContext?.permissions?.includes(Permissions.USERS_VIEW)) {
      CallQueuesLinks.push(CallQueuesUsersLink)
    }

    if (userContext?.permissions?.includes(Permissions.GENERAL_SETTINGS_VIEW)) {
      CallQueuesLinks.push(CallQueuesSettingsLink)
    }

    return CallQueuesLinks
  }, [t, userContext, SkeletonLink])
}

export default useCallQueuesLinks
